import { useMemo } from "react";
import { useSelector } from "react-redux";

import { useConfig } from "@stadium/hooks/lib/use-config";
import { useStoreContext } from "@components/store/provider";

import {
  getGlobalCountryZoneId,
  selectAllCountries,
} from "@stadium/redux-utils/selectors/configSelector";
import { getIsBundleShop, getIsLockerShop, getStoreBlacklistedCountries } from "@selectors/store";

import ToolTip from "@stadium/ui/src/components/tooltip/tooltip";
import { InternalButton } from "@sharedComponents/buttons";

import { getCountryFlagUrl } from "@stadium/ui/src/shared/country-flag";

import { GLOBAL_COUNTRY, KIT_COUNTRIES } from "@constants/country";

import styles from "./main.module.scss";

const useAllCountries = ({
  includeGlobalCountry = false,
  withFlags = false,
  showGlobalCountryAtTop = false,
} = {}) => {
  const isLockerShop = useSelector(getIsLockerShop);
  const blacklistedCountries = useSelector(getStoreBlacklistedCountries);
  const allCountries = useConfig(selectAllCountries);

  const countriesList = useMemo(() => {
    const blacklistedCountriesSet = new Set(blacklistedCountries.map((c) => c.toLowerCase()));
    return allCountries.filter((c) => !blacklistedCountriesSet.has(c.iso.toLowerCase()));
  }, [allCountries, blacklistedCountries]);

  const globalCountryZoneId = useConfig(getGlobalCountryZoneId);
  const isKitShop = useSelector(getIsBundleShop);

  const { isKitRedemption } = useStoreContext();

  const allCountriesList = useMemo(() => {
    let list;
    if (isKitShop && !isKitRedemption) {
      list = KIT_COUNTRIES;
    } else if (isLockerShop || includeGlobalCountry) {
      const globalCountryOption = {
        ...GLOBAL_COUNTRY,
        zoneId: globalCountryZoneId,
        label: (
          <div className={styles.globalCountryWrap}>
            Global{" "}
            <InternalButton
              styleType="no-style"
              onClick={(e) => e.stopPropagation()}
              className={styles.infoIcon}
            >
              <ToolTip
                containerClass={styles.toolTipWrapper}
                content="These items are available to recipients in all countries."
                isLightTheme
                placement="top-end"
                arrow
                infoIcon
              />
            </InternalButton>
          </div>
        ),
        showLabel: true,
      };
      list = showGlobalCountryAtTop
        ? [globalCountryOption, ...countriesList]
        : [...countriesList, globalCountryOption];
    } else {
      list = countriesList;
    }

    if (!withFlags) return list;
    return list.map((country) => ({
      ...country,
      flagUrl: getCountryFlagUrl({ iso: country.iso }, "round"),
    }));
  }, [
    countriesList,
    isLockerShop,
    includeGlobalCountry,
    globalCountryZoneId,
    isKitShop,
    withFlags,
  ]);

  return {
    allCountriesList,
  };
};

export default useAllCountries;
